import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Zap, TrendingUp, Bookmark, Sparkles } from "lucide-react";
import { APP } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  "AI & Tech",
  "Sustainability",
  "Health & Wellness",
  "Pet Economy",
  "Silver Tech",
  "Experience & Education",
  "Local Services",
  "Creator Economy",
  "FinTech",
  "Climate Tech",
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  const goToApp = () => nav(user ? "/app" : "/auth?mode=signup");

  return (
    <div className="min-h-screen grain relative">
      {/* Nav */}
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-[var(--bg)]/80 border-b border-[color:var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid={APP.navLogo} className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[color:var(--fg)] text-[color:var(--bg)] flex items-center justify-center font-display text-sm">T</div>
            <span className="font-display text-lg tracking-tight">TRENDING/IDEAS</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <button
                data-testid={APP.navDashboard}
                onClick={() => nav("/app")}
                className="px-5 py-2.5 bg-[color:var(--fg)] text-[color:var(--bg)] font-semibold text-sm uppercase tracking-wider hover:opacity-90"
              >
                Dashboard →
              </button>
            ) : (
              <>
                <Link
                  to="/auth?mode=login"
                  data-testid={APP.navLogin}
                  className="hidden sm:inline-block px-4 py-2.5 text-sm font-semibold uppercase tracking-wider hover:underline"
                >
                  Log in
                </Link>
                <Link
                  to="/auth?mode=signup"
                  data-testid={APP.navSignup}
                  className="px-5 py-2.5 bg-[color:var(--fg)] text-[color:var(--bg)] font-semibold text-sm uppercase tracking-wider hover:opacity-90"
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section
        data-testid={APP.landingHero}
        className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16"
      >
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="tag mb-8 bg-[color:var(--yellow)] border-[color:var(--fg)] text-[color:var(--fg)]">
              <Zap size={12} strokeWidth={2.5} /> UPDATED FOR 2026
            </div>
            <h1 className="font-display text-[clamp(3rem,9vw,8rem)] leading-[0.88] tracking-[-0.04em]">
              Stop guessing.<br />
              <span className="italic font-light" style={{ fontFamily: "Cabinet Grotesk" }}>
                Build what&apos;s{" "}
              </span>
              <span className="relative inline-block">
                trending
                <span className="absolute -bottom-2 left-0 right-0 h-3 bg-[color:var(--hot)] -z-10" />
              </span>
              .
            </h1>
            <p className="mt-10 text-lg lg:text-xl max-w-2xl text-[color:var(--muted)] leading-relaxed">
              AI-curated business ideas grounded in <span className="text-[color:var(--fg)] font-semibold">live signals</span> from Hacker News, DEV.to, and GitHub Trending.
              Localized to your country & currency. Filter, deep-dive, and save what&apos;s worth building.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="tag bg-[color:var(--yellow)] border-[color:var(--fg)] text-[color:var(--fg)]">100% FREE · BETA</span>
              <span className="text-[color:var(--muted)]">while we gather feedback</span>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid={APP.landingCtaPrimary}
                onClick={goToApp}
                className="group px-8 py-4 bg-[color:var(--fg)] text-[color:var(--bg)] font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-3 brutal-shadow"
              >
                Generate ideas
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-200" />
              </button>
              <a
                href="#how"
                data-testid={APP.landingCtaSecondary}
                className="px-6 py-4 font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2 border border-[color:var(--fg)] hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] transition-colors"
              >
                How it works
              </a>
            </div>
          </div>

          {/* Bento tile */}
          <div className="col-span-12 lg:col-span-4">
            <div className="brutal-border bg-[color:var(--bg)] p-6 h-full flex flex-col justify-between min-h-[320px] relative overflow-hidden">
              <div className="stripes absolute top-0 right-0 w-20 h-20 opacity-10" />
              <div>
                <div className="text-xs font-mono-tight text-[color:var(--muted)] uppercase tracking-widest">// live signal</div>
                <div className="mt-4 font-display text-4xl leading-none">92</div>
                <div className="text-xs uppercase tracking-widest mt-1 text-[color:var(--muted)]">trend score</div>
              </div>
              <div className="mt-6 border-t border-[color:var(--line)] pt-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">Hottest niche</div>
                <div className="font-display text-2xl mt-1">AI Ops for SMBs</div>
                <div className="text-sm text-[color:var(--muted)] mt-2">Vertical AI SaaS eating traditional service work.</div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="tag">saas</span>
                <span className="tag">b2b</span>
                <span className="tag">high margin</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="mt-16 border-y border-[color:var(--fg)] py-4 overflow-hidden">
          <div className="ticker-track whitespace-nowrap font-display text-2xl uppercase">
            {[...CATEGORIES, ...CATEGORIES].map((c, i) => (
              <span key={`ticker-${i}-${c}`} className="mx-8 inline-flex items-center gap-4">
                <TrendingUp size={18} />
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4">
            <div className="tag mb-4">// process</div>
            <h2 className="font-display text-5xl lg:text-6xl leading-none">
              Three moves.<br />No fluff.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-8 grid sm:grid-cols-3 gap-4">
            {[
              { n: "01", icon: Sparkles, t: "Filter", d: "Pick your budget, skills, and market scope. We match ideas to your reality — not someone else&apos;s dream." },
              { n: "02", icon: TrendingUp, t: "Generate", d: "Gemini 3 pulls fresh signals across niches with real competitors, revenue models, and first steps." },
              { n: "03", icon: Bookmark, t: "Save", d: "Bookmark the ones worth chasing. Add notes. Come back when you're ready to build." },
            ].map((s) => (
              <div key={s.n} className="brutal-border p-6 relative hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] transition-colors group">
                <div className="font-mono-tight text-xs opacity-60">{s.n}</div>
                <s.icon size={28} strokeWidth={1.5} className="mt-3" />
                <div className="font-display text-2xl mt-4">{s.t}</div>
                <p className="text-sm mt-2 leading-relaxed opacity-80">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories showcase */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-[color:var(--line)]">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="tag mb-3">// coverage</div>
            <h2 className="font-display text-5xl leading-none">Niches we track</h2>
          </div>
          <div className="text-sm text-[color:var(--muted)] max-w-md">
            From vertical AI SaaS to aging-in-place services — we watch the categories with real tailwinds in 2026.
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-t border-l border-[color:var(--fg)]">
          {CATEGORIES.map((c) => (
            <div
              key={c}
              className="p-6 border-r border-b border-[color:var(--fg)] hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] cursor-default transition-colors"
            >
              <div className="font-mono-tight text-xs opacity-60">/{c.split(" ")[0].toLowerCase()}</div>
              <div className="font-display text-xl mt-2 leading-tight">{c}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="bg-[color:var(--fg)] text-[color:var(--bg)] p-10 lg:p-16 relative overflow-hidden">
          <div className="stripes absolute inset-0 opacity-5" />
          <div className="relative">
            <div className="font-mono-tight text-xs uppercase tracking-widest opacity-60">// stop scrolling</div>
            <h2 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.9]">
              Your next<br />business is<br />
              <span className="text-[color:var(--yellow)]">one filter</span> away.
            </h2>
            <button
              onClick={goToApp}
              data-testid="landing-cta-bottom"
              className="mt-10 px-8 py-4 bg-[color:var(--yellow)] text-[color:var(--fg)] font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-3 hover:opacity-90"
            >
              Start free →
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-[color:var(--line)] py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between text-xs uppercase tracking-widest text-[color:var(--muted)]">
          <div>Trending/Ideas © 2026</div>
          <div className="font-mono-tight">v.1.0 · gemini 3 flash</div>
        </div>
      </footer>
    </div>
  );
}
